import { useState } from "react";
import { Alert, Badge, Button, Icon, Loading, Modal } from "@/deps/ui-library";
import { api } from "@/api";
import type { CheckoutOrder, PaymentMethod } from "@/api/types";
import { SegmentControl } from "@/components/SegmentControl";
import { formatAmount } from "@/lib/money";
import { goToGateway, methodLabel, onlineMethods } from "./paymentMethods";

type Stage = "choose" | "opening" | "leaving" | "confirming" | "declined";

/**
 * The payment page (plan slice 10.7, the design's R3): every immediate charge goes through it. The customer picks eSewa,
 * Khalti or connectIPS (suite ADR-0102), and is sent to that gateway's page; coming back lands on the payment return
 * page, which asks RigoSub whether it was paid. An order is opened with one gateway, so picking another places it again
 * through `replace`, which cancels the first: nothing was charged for it.
 *
 * In development every gateway is a simulated stand-in: no money moves, and this page offers approve and decline in
 * place of the gateway's page.
 */
export function PaymentPage({
  order,
  replace,
  onClose,
  onPaid,
}: {
  order: CheckoutOrder;
  /** Places the same order again through another method; without it, the order's method is the only one. */
  replace?: (method: PaymentMethod) => Promise<CheckoutOrder>;
  onClose: () => void;
  onPaid: () => void;
}) {
  const [current, setCurrent] = useState(order);
  const [stage, setStage] = useState<Stage>("choose");
  const offered = onlineMethods(current.quote.paymentMethods);
  const methods = replace ? offered : offered.filter((m) => m.value === current.payment.gateway);
  const [method, setMethod] = useState<PaymentMethod>(
    (methods.find((m) => m.value === current.payment.gateway)?.value ??
      methods[0]?.value ??
      current.payment.gateway) as PaymentMethod,
  );
  const [message, setMessage] = useState<string | null>(null);
  const amount = `${current.quote.currency} ${formatAmount(current.quote.total)}`;
  const busy = stage === "opening" || stage === "leaving" || stage === "confirming";

  /** The order, opened with the chosen method: placed again when it was opened with another. */
  const opened = async (): Promise<CheckoutOrder | null> => {
    if (method === current.payment.gateway || !replace) return current;
    setStage("opening");
    try {
      const again = await replace(method);
      setCurrent(again);
      return again;
    } catch (failure) {
      setMessage(failure instanceof Error ? failure.message : "The payment couldn't be started.");
      setStage("declined");
      return null;
    }
  };

  const answer = async (target: CheckoutOrder, callback: "approve" | "decline") => {
    setStage("confirming");
    setMessage(null);
    try {
      const result = await api.subscription.confirmPayment(target.checkoutId, callback);
      if (result.outcome === "PAID") {
        onPaid();
        return;
      }
      setMessage(result.message ?? "The payment was declined. Nothing was charged.");
      setStage("declined");
    } catch (failure) {
      setMessage(failure instanceof Error ? failure.message : "The payment couldn't be confirmed.");
      setStage("declined");
    }
  };

  const pay = async (callback: "approve" | "decline" = "approve") => {
    const target = await opened();
    if (!target) return;
    if (target.payment.simulated) {
      await answer(target, callback);
      return;
    }
    setStage("leaving");
    goToGateway(target.payment);
  };

  return (
    <Modal.Root
      open
      onOpenChange={(details) => {
        if (!details.open && !busy) onClose();
      }}
      closeOnInteractOutside={false}
      lazyMount
      unmountOnExit
      trapFocus
    >
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner centered scrollMode="inside">
          <Modal.Content size="md" scrollMode="inside">
            <div className="flex items-center justify-between gap-2 pr-8">
              <Modal.Title size="sm">Pay</Modal.Title>
              {current.payment.simulated ? (
                <Badge colorScheme="warning" size="sm">
                  Simulated
                </Badge>
              ) : null}
            </div>
            <Modal.Description size="sm">
              {current.payment.simulated
                ? `${methodLabel(method)}'s stand-in for development. No money moves, whichever you press.`
                : `You finish on ${methodLabel(method)}'s page, then come back here.`}
            </Modal.Description>

            <Modal.Body className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-1 py-2">
                <span className="text-fg num text-2xl font-semibold">{amount}</span>
                <span className="text-fg-muted text-xs">
                  {current.quote.taxPercent > 0
                    ? `Includes VAT ${current.quote.taxPercent}%`
                    : `Charged in ${current.quote.currency}`}
                </span>
              </div>

              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-fg-muted">For</dt>
                <dd className="text-fg m-0">
                  {current.quote.lines.map((line) => line.label).join(", ")}
                </dd>
                <dt className="text-fg-muted">Reference</dt>
                <dd className="text-fg m-0">{current.payment.reference}</dd>
              </dl>

              {stage === "confirming" || stage === "opening" || stage === "leaving" ? (
                <div role="status" className="flex flex-col items-center gap-2 py-4">
                  <Loading />
                  <span className="text-fg-muted text-sm">
                    {stage === "confirming"
                      ? "Confirming the payment. This takes a few seconds."
                      : `Opening ${methodLabel(method)}…`}
                  </span>
                </div>
              ) : stage === "declined" ? (
                <Alert
                  config={{ variant: "subtle", colorScheme: "danger" }}
                  title="The payment didn't go through"
                  description={`${message ?? "Nothing was charged."} Try again, or choose another way to pay.`}
                />
              ) : methods.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {methods.length > 1 ? (
                    <SegmentControl<PaymentMethod>
                      label="Pay with"
                      segments={methods}
                      value={method}
                      onChange={setMethod}
                    />
                  ) : null}
                  <p className="text-fg-muted m-0 flex items-start gap-2 text-sm">
                    <Icon name="ShieldCheck" size="sm" />
                    {methods.find((m) => m.value === method)?.how}
                  </p>
                </div>
              ) : (
                <Alert
                  config={{ variant: "subtle", colorScheme: "warning" }}
                  description={`There's no way to pay ${current.quote.currency} online yet.`}
                />
              )}
            </Modal.Body>

            <Modal.Footer align="right">
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={onClose}
              >
                Cancel
              </Button>
              {stage === "declined" ? (
                <Button colorScheme="primary" size="sm" onClick={() => setStage("choose")}>
                  Try again
                </Button>
              ) : (
                <>
                  {current.payment.simulated ? (
                    <Button
                      colorScheme="default"
                      variant="outline"
                      size="sm"
                      disabled={busy || methods.length === 0}
                      onClick={() => void pay("decline")}
                    >
                      Simulate a decline
                    </Button>
                  ) : null}
                  <Button
                    colorScheme="primary"
                    size="sm"
                    loading={busy}
                    disabled={methods.length === 0}
                    onClick={() => void pay()}
                  >
                    {current.payment.simulated
                      ? `Pay ${amount}`
                      : `Pay ${amount} with ${methodLabel(method)}`}
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}
